import { useMutation, useQuery } from "@tanstack/react-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import { type FormEvent, useMemo, useState } from "react"
import { ReceiveScanPanel } from "@/inventory/components/receive-scan-panel"
import {
  addSerialsToDraftFn,
  commitBatchFn,
  createDraftBatchFn,
  discardDraftBatchFn,
  listCarriersFn,
  listProductsFn,
  listVendorsFn,
  removeSerialFromDraftFn,
  updateDraftBatchFn,
} from "@/inventory/rpc"
import { Button } from "@/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/ui/card"
import { Input } from "@/ui/input"
import { Label } from "@/ui/label"
import { cn } from "@/ui/utils"

export const Route = createFileRoute("/_inventory-layout/inventory/receive")({
  component: ReceivePage,
})

interface BatchSerial {
  id: number
  serialNumber: string
}

interface ReceiveBatch {
  id: number
  vendorId: number
  productId: number
  carrierId: number | null
  invoiceNumber: string
  unitCostCents: number | null
  notes: string | null
  status: string
  serials: BatchSerial[]
}

interface MetaFormState {
  vendorId: string
  productId: string
  carrierId: string
  invoiceNumber: string
  unitCostUsd: string
  notes: string
}

const emptyMeta: MetaFormState = {
  vendorId: "",
  productId: "",
  carrierId: "",
  invoiceNumber: "",
  unitCostUsd: "",
  notes: "",
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message
  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as { message: unknown }).message === "string"
  ) {
    return (error as { message: string }).message
  }
  return "Something went wrong"
}

function centsToUsdInput(cents: number | null | undefined): string {
  if (cents == null) return ""
  return (cents / 100).toFixed(2)
}

function parseUnitCostCents(usd: string): number | null {
  const trimmed = usd.trim()
  if (!trimmed) return null
  const dollars = Number.parseFloat(trimmed)
  if (!Number.isFinite(dollars) || dollars < 0) {
    throw new Error("Unit cost must be a non-negative number")
  }
  return Math.round(dollars * 100)
}

function metaFromBatch(batch: ReceiveBatch): MetaFormState {
  return {
    vendorId: String(batch.vendorId),
    productId: String(batch.productId),
    carrierId: batch.carrierId != null ? String(batch.carrierId) : "",
    invoiceNumber: batch.invoiceNumber,
    unitCostUsd: centsToUsdInput(batch.unitCostCents),
    notes: batch.notes ?? "",
  }
}

function selectClassName() {
  return cn(
    "flex h-9 w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-xs outline-none transition-[color,box-shadow] md:text-sm dark:bg-input/30",
    "focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
    "disabled:cursor-not-allowed disabled:opacity-50",
  )
}

function ReceivePage() {
  const [batch, setBatch] = useState<ReceiveBatch | null>(null)
  const [meta, setMeta] = useState<MetaFormState>(emptyMeta)
  const [formError, setFormError] = useState<string | null>(null)
  const [scanError, setScanError] = useState<string | null>(null)
  const [scanWarning, setScanWarning] = useState<string | null>(null)
  const [success, setSuccess] = useState<{
    batchId: number
    count: number
    vendorId: number
    invoiceNumber: string
  } | null>(null)

  const vendorsQuery = useQuery({
    queryKey: ["vendors", { includeInactive: false }],
    queryFn: () => listVendorsFn({ data: { includeInactive: false } }),
  })

  const productsQuery = useQuery({
    queryKey: ["products", { includeInactive: false }],
    queryFn: () => listProductsFn({ data: { includeInactive: false } }),
  })

  const carriersQuery = useQuery({
    queryKey: ["carriers", { includeInactive: false }],
    queryFn: () => listCarriersFn({ data: { includeInactive: false } }),
  })

  const vendors = vendorsQuery.data ?? []
  const products = productsQuery.data ?? []
  const carriers = carriersQuery.data ?? []

  const vendorNameById = useMemo(() => {
    const map = new Map<number, string>()
    for (const v of vendors) map.set(v.id, v.name)
    return map
  }, [vendors])

  const productNameById = useMemo(() => {
    const map = new Map<number, string>()
    for (const p of products) map.set(p.id, p.name)
    return map
  }, [products])

  const carrierNameById = useMemo(() => {
    const map = new Map<number, string>()
    for (const c of carriers) map.set(c.id, c.name)
    return map
  }, [carriers])

  const serials = useMemo(
    () => (batch?.serials ?? []).map((s) => s.serialNumber),
    [batch],
  )

  const createDraft = useMutation({
    mutationFn: async (values: MetaFormState) => {
      const unitCostCents = parseUnitCostCents(values.unitCostUsd)
      if (!values.vendorId || !values.productId) {
        throw new Error("Vendor and product are required")
      }
      if (!values.invoiceNumber.trim()) {
        throw new Error("Invoice number is required")
      }
      return createDraftBatchFn({
        data: {
          vendorId: Number(values.vendorId),
          productId: Number(values.productId),
          invoiceNumber: values.invoiceNumber.trim(),
          unitCostCents,
          notes: values.notes.trim() || null,
          carrierId: values.carrierId ? Number(values.carrierId) : null,
        },
      })
    },
    onSuccess: (created) => {
      setFormError(null)
      setScanError(null)
      setScanWarning(null)
      setSuccess(null)
      const next = created as ReceiveBatch
      setBatch(next)
      setMeta(metaFromBatch(next))
    },
    onError: (error) => setFormError(getErrorMessage(error)),
  })

  const updateDraft = useMutation({
    mutationFn: async (values: MetaFormState) => {
      if (!batch) throw new Error("No draft batch")
      const unitCostCents = parseUnitCostCents(values.unitCostUsd)
      if (!values.vendorId || !values.productId) {
        throw new Error("Vendor and product are required")
      }
      if (!values.invoiceNumber.trim()) {
        throw new Error("Invoice number is required")
      }
      return updateDraftBatchFn({
        data: {
          batchId: batch.id,
          vendorId: Number(values.vendorId),
          productId: Number(values.productId),
          invoiceNumber: values.invoiceNumber.trim(),
          unitCostCents,
          notes: values.notes.trim() || null,
          carrierId: values.carrierId ? Number(values.carrierId) : null,
        },
      })
    },
    onSuccess: (updated) => {
      setFormError(null)
      const next = updated as ReceiveBatch
      setBatch(next)
      setMeta(metaFromBatch(next))
    },
    onError: (error) => setFormError(getErrorMessage(error)),
  })

  const addSerial = useMutation({
    mutationFn: (serial: string) => {
      if (!batch) throw new Error("No draft batch")
      return addSerialsToDraftFn({
        data: { batchId: batch.id, serials: [serial] },
      })
    },
    onSuccess: (result) => {
      setScanError(null)
      const next = result.batch as ReceiveBatch
      setBatch(next)
      if (result.alreadyInBatch.length > 0) {
        setScanWarning(
          `Already in this batch (skipped): ${result.alreadyInBatch.join(", ")}`,
        )
      } else {
        setScanWarning(null)
      }
    },
    onError: (error) => setScanError(getErrorMessage(error)),
  })

  const removeSerial = useMutation({
    mutationFn: (serialNumber: string) => {
      if (!batch) throw new Error("No draft batch")
      return removeSerialFromDraftFn({
        data: { batchId: batch.id, serialNumber },
      })
    },
    onSuccess: (updated) => {
      setScanError(null)
      setScanWarning(null)
      setBatch(updated as ReceiveBatch)
    },
    onError: (error) => setScanError(getErrorMessage(error)),
  })

  const commit = useMutation({
    mutationFn: () => {
      if (!batch) throw new Error("No draft batch")
      return commitBatchFn({ data: { batchId: batch.id } })
    },
    onSuccess: (committed) => {
      const next = committed as ReceiveBatch
      setFormError(null)
      setScanError(null)
      setScanWarning(null)
      setSuccess({
        batchId: next.id,
        count: next.serials.length,
        vendorId: next.vendorId,
        invoiceNumber: next.invoiceNumber,
      })
      setBatch(null)
    },
    onError: (error) => setScanError(getErrorMessage(error)),
  })

  const discard = useMutation({
    mutationFn: () => {
      if (!batch) throw new Error("No draft batch")
      return discardDraftBatchFn({ data: { batchId: batch.id } })
    },
    onSuccess: () => {
      setFormError(null)
      setScanError(null)
      setScanWarning(null)
      setBatch(null)
      setMeta(emptyMeta)
    },
    onError: (error) => setFormError(getErrorMessage(error)),
  })

  const catalogsLoading =
    vendorsQuery.isLoading || productsQuery.isLoading || carriersQuery.isLoading

  const catalogsError = vendorsQuery.isError
    ? getErrorMessage(vendorsQuery.error)
    : productsQuery.isError
      ? getErrorMessage(productsQuery.error)
      : carriersQuery.isError
        ? getErrorMessage(carriersQuery.error)
        : null

  // Form / commit / discard: block concurrent meta actions
  const formBusy =
    createDraft.isPending ||
    updateDraft.isPending ||
    commit.isPending ||
    discard.isPending
  // Include serial mutations for commit/discard/meta only — never disable the scan input
  const busy = formBusy || addSerial.isPending || removeSerial.isPending

  function handleMetaChange<K extends keyof MetaFormState>(
    key: K,
    value: MetaFormState[K],
  ) {
    setMeta((prev) => ({ ...prev, [key]: value }))
  }

  async function handleCreateSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setFormError(null)
    try {
      await createDraft.mutateAsync(meta)
    } catch {
      // surfaced via onError
    }
  }

  async function handleUpdateSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setFormError(null)
    try {
      await updateDraft.mutateAsync(meta)
    } catch {
      // surfaced via onError
    }
  }

  function handleStartAnother() {
    if (!success) return
    setMeta({
      ...emptyMeta,
      vendorId: String(success.vendorId),
      invoiceNumber: success.invoiceNumber,
    })
    setSuccess(null)
    setBatch(null)
    setFormError(null)
    setScanError(null)
    setScanWarning(null)
  }

  function handleStartFresh() {
    setSuccess(null)
    setBatch(null)
    setMeta(emptyMeta)
    setFormError(null)
    setScanError(null)
    setScanWarning(null)
  }

  if (success) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="font-semibold text-2xl tracking-tight">Receive</h1>
          <p className="mt-1 text-muted-foreground text-sm">
            Stock received into inventory.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Batch committed</CardTitle>
            <CardDescription>
              {success.count} unit{success.count === 1 ? "" : "s"} created as
              available from batch #{success.batchId}.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            <Button asChild>
              <Link to="/inventory/units">View units</Link>
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleStartAnother}
            >
              Start another
            </Button>
            <Button type="button" variant="ghost" onClick={handleStartFresh}>
              New receive (clear form)
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-semibold text-2xl tracking-tight">Receive</h1>
        <p className="mt-1 text-muted-foreground text-sm">
          Create a draft batch, scan serials, then commit into inventory.
        </p>
      </div>

      {catalogsError ? (
        <p className="text-destructive text-sm" role="alert">
          {catalogsError}
        </p>
      ) : null}

      {catalogsLoading ? (
        <p className="text-muted-foreground text-sm">Loading catalogs…</p>
      ) : null}

      {!batch ? (
        <Card>
          <CardHeader>
            <CardTitle>Batch details</CardTitle>
            <CardDescription>
              Vendor, invoice, product, and optional cost/carrier before
              scanning.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <MetaForm
              meta={meta}
              vendors={vendors}
              products={products}
              carriers={carriers}
              disabled={busy || catalogsLoading}
              error={formError}
              submitLabel={
                createDraft.isPending ? "Starting…" : "Start scanning"
              }
              onChange={handleMetaChange}
              onSubmit={handleCreateSubmit}
            />
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Batch #{batch.id}</CardTitle>
              <CardDescription>
                Draft — update details or discard before commit.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <dl className="grid gap-2 text-sm sm:grid-cols-2">
                <div>
                  <dt className="text-muted-foreground">Vendor</dt>
                  <dd className="font-medium">
                    {vendorNameById.get(batch.vendorId) ??
                      `Vendor #${batch.vendorId}`}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Product</dt>
                  <dd className="font-medium">
                    {productNameById.get(batch.productId) ??
                      `Product #${batch.productId}`}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Invoice</dt>
                  <dd className="font-medium">{batch.invoiceNumber}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Carrier</dt>
                  <dd className="font-medium">
                    {batch.carrierId != null
                      ? (carrierNameById.get(batch.carrierId) ??
                        `Carrier #${batch.carrierId}`)
                      : "—"}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Unit cost</dt>
                  <dd className="font-medium">
                    {batch.unitCostCents != null
                      ? `$${(batch.unitCostCents / 100).toFixed(2)}`
                      : "—"}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Serials</dt>
                  <dd className="font-medium tabular-nums">{serials.length}</dd>
                </div>
              </dl>

              <details className="rounded-md border px-3 py-2">
                <summary className="cursor-pointer font-medium text-sm">
                  Edit batch details
                </summary>
                <div className="mt-4">
                  <MetaForm
                    meta={meta}
                    vendors={vendors}
                    products={products}
                    carriers={carriers}
                    disabled={busy}
                    error={null}
                    submitLabel={
                      updateDraft.isPending ? "Saving…" : "Save changes"
                    }
                    onChange={handleMetaChange}
                    onSubmit={handleUpdateSubmit}
                  />
                </div>
              </details>

              {formError ? (
                <p className="text-destructive text-sm" role="alert">
                  {formError}
                </p>
              ) : null}

              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  onClick={() => void commit.mutateAsync()}
                  disabled={busy || serials.length === 0}
                >
                  {commit.isPending ? "Committing…" : "Commit batch"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    if (
                      !window.confirm(
                        "Discard this draft batch and all scanned serials?",
                      )
                    ) {
                      return
                    }
                    void discard.mutateAsync()
                  }}
                  disabled={busy}
                >
                  {discard.isPending ? "Discarding…" : "Discard draft"}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Scan serials</CardTitle>
              <CardDescription>
                Keyboard wedge / scan gun: keep focus in the field and press
                Enter after each serial.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {scanWarning ? (
                <p className="text-amber-700 text-sm dark:text-amber-400">
                  {scanWarning}
                </p>
              ) : null}
              <ReceiveScanPanel
                serials={serials}
                onAdd={(serial) => {
                  setScanError(null)
                  // Soft-flag local duplicates without waiting on RPC
                  if (serials.includes(serial)) {
                    setScanWarning(`Already in this batch (skipped): ${serial}`)
                    return
                  }
                  void addSerial.mutateAsync(serial)
                }}
                onRemove={(serial) => {
                  void removeSerial.mutateAsync(serial)
                }}
                error={scanError}
                // Keep scan input enabled during add/remove for continuous wedge scanning
                disabled={formBusy}
              />
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}

interface NamedOption {
  id: number
  name: string
  isActive?: boolean
  type?: string
}

interface MetaFormProps {
  meta: MetaFormState
  vendors: NamedOption[]
  products: NamedOption[]
  carriers: NamedOption[]
  disabled?: boolean
  error?: string | null
  submitLabel: string
  onChange: <K extends keyof MetaFormState>(
    key: K,
    value: MetaFormState[K],
  ) => void
  onSubmit: (event: FormEvent<HTMLFormElement>) => void
}

function MetaForm({
  meta,
  vendors,
  products,
  carriers,
  disabled = false,
  error = null,
  submitLabel,
  onChange,
  onSubmit,
}: MetaFormProps) {
  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="grid gap-1.5">
          <Label htmlFor="receive-vendor">Vendor</Label>
          <select
            id="receive-vendor"
            value={meta.vendorId}
            onChange={(e) => onChange("vendorId", e.target.value)}
            disabled={disabled}
            required
            className={selectClassName()}
          >
            <option value="">Select vendor…</option>
            {vendors.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </select>
        </div>

        <div className="grid gap-1.5">
          <Label htmlFor="receive-product">Product</Label>
          <select
            id="receive-product"
            value={meta.productId}
            onChange={(e) => onChange("productId", e.target.value)}
            disabled={disabled}
            required
            className={selectClassName()}
          >
            <option value="">Select product…</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
                {p.type ? ` (${p.type})` : ""}
              </option>
            ))}
          </select>
        </div>

        <div className="grid gap-1.5">
          <Label htmlFor="receive-invoice">Invoice number</Label>
          <Input
            id="receive-invoice"
            value={meta.invoiceNumber}
            onChange={(e) => onChange("invoiceNumber", e.target.value)}
            disabled={disabled}
            autoComplete="off"
            required
          />
        </div>

        <div className="grid gap-1.5">
          <Label htmlFor="receive-cost">Unit cost (USD)</Label>
          <Input
            id="receive-cost"
            type="number"
            inputMode="decimal"
            min={0}
            step="0.01"
            placeholder="0.00"
            value={meta.unitCostUsd}
            onChange={(e) => onChange("unitCostUsd", e.target.value)}
            disabled={disabled}
            autoComplete="off"
          />
        </div>

        <div className="grid gap-1.5">
          <Label htmlFor="receive-carrier">Carrier</Label>
          <select
            id="receive-carrier"
            value={meta.carrierId}
            onChange={(e) => onChange("carrierId", e.target.value)}
            disabled={disabled}
            className={selectClassName()}
          >
            <option value="">None</option>
            {carriers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <div className="grid gap-1.5 sm:col-span-2">
          <Label htmlFor="receive-notes">Notes</Label>
          <Input
            id="receive-notes"
            value={meta.notes}
            onChange={(e) => onChange("notes", e.target.value)}
            disabled={disabled}
            autoComplete="off"
            placeholder="Optional"
          />
        </div>
      </div>

      {error ? (
        <p className="text-destructive text-sm" role="alert">
          {error}
        </p>
      ) : null}

      <div>
        <Button type="submit" disabled={disabled}>
          {submitLabel}
        </Button>
      </div>
    </form>
  )
}
